import React, { useState } from 'react';

// A chat-embedded, collapsible result card — same classify-res-comp classes (index.css)
// EARChatUI's ProductProfileResults/ScheduleBResults use for their own phase cards, so the
// Export Design Advisor's "Product Specs" / "Key Specs Table" cards look consistent with
// the sibling export workflow rather than inventing new chrome.

interface DetailRowData {
  label: string;
  value?: React.ReactNode;
}

export interface DetailSection {
  title?: string;
  rows: DetailRowData[];
}

interface ExportDesignPhaseCardProps {
  title: string;
  complete: boolean;
  collapsedSummary: string;
  sections: DetailSection[];
  defaultExpanded?: boolean;
}

const DetailRow: React.FC<DetailRowData> = ({ label, value }) => (
  <div className='classify-res-comp-sec-col'>
    <div className='classify-res-comp-sec'>
      <div className='classify-res-comp-sec-txt'>{label}</div>
    </div>
    <div>
      <h2
        style={{
          color: 'var(--Text-Main, var(--text-primary))',
          fontSize: '14px',
          fontWeight: 400,
          paddingLeft: '4px',
        }}
      >
        {value || 'N/A'}
      </h2>
    </div>
  </div>
);

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div className='classify-res-comp-sec' style={{ paddingTop: '4px' }}>
    <div className='classify-res-comp-sec-txt' style={{ fontWeight: 600, paddingLeft: '4px' }}>
      {title}
    </div>
  </div>
);

const Divider: React.FC = () => (
  <div className='export-divider' style={{ display: 'flex' }} />
);

const ExportDesignPhaseCard: React.FC<ExportDesignPhaseCardProps> = ({
  title,
  complete,
  collapsedSummary,
  sections,
  defaultExpanded = false,
}) => {
  const [isExpand, setIsExpand] = useState(defaultExpanded);
  const toggle = () => setIsExpand((prev) => !prev);

  return (
    <div className='classify-res-comp'>
      {/* A real control: it can be reached and operated from the keyboard, and says whether the
          card is open (it was a bare div with a click handler). */}
      <div
        className='classify-res-comp-header'
        role='button'
        tabIndex={0}
        aria-expanded={isExpand}
        onClick={toggle}
        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        <div className='classify-res-comp-header-text'>{title}</div>
        <div className='classify-res-comp-header-left-icon' style={{ paddingTop: '2px' }}>
          {complete ? (
            <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
              <path
                d='M13.3327 4L5.99935 11.3333L2.66602 8'
                stroke='#17B26A'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          ) : (
            <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
              <path
                d='M8.00016 3.99992V7.99992L10.6668 9.33325M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z'
                stroke='#F79009'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          )}
        </div>
        <div className='classify-res-comp-header-left-icon' style={{ paddingTop: '2px' }}>
          <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
            <path
              d={isExpand ? 'M4 6L8 10L12 6' : 'M10 12L6 8L10 4'}
              stroke='var(--text-classify)'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </div>

      {!isExpand ? (
        <p className='classification-status' onClick={toggle} style={{ cursor: 'pointer' }}>
          {collapsedSummary}
        </p>
      ) : (
        sections.map((section, sectionIndex) => (
          <React.Fragment key={`${title}-section-${sectionIndex}`}>
            {section.title && <SectionHeader title={section.title} />}
            {section.rows.map((row, rowIndex) => (
              <React.Fragment key={`${title}-row-${sectionIndex}-${rowIndex}`}>
                <DetailRow label={row.label} value={row.value} />
                <Divider />
              </React.Fragment>
            ))}
          </React.Fragment>
        ))
      )}
    </div>
  );
};

export default ExportDesignPhaseCard;
